import React, { useRef, useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Link,
  CircularProgress,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DashboardBreadcrumbs from "../../components/DashboardBreadcrumbs";
import { Icon as OpusIcon } from "../../components/common/opus";
import { CaptionEditor } from "@ae/ograf-caption-builder";
import { layerMeta } from "../../services/scenes/captionPayload";
import AssetExplorer from "../../components/common/AssetExplorer";
import {
  logicalToRelative as assetExplorerLogicalToRelative,
  urlSegmentToLogical as assetExplorerUrlSegmentToLogical,
} from "../../components/common/AssetExplorer/pathUtils";
import MaestroFormPreview from "../../components/MaestroFormPreview";
import darkTheme from "../../style/theme";
import apiService from "../../services/api.service";
import authService from "../../services/auth.service";
import S3ApiService from "../../services/s3-api.service";
import { useAlert } from "../../contexts/AlertContext";
import * as sceneCache from "../../utils/sceneDataCache";
import plsFootballData from "../../constants/pls-football.data";
import { dds } from "../../dds/ddsClient";
import { fixtureListPreset } from "@aelive/dds-client";

const toPathSegment = (name) =>
  (name || "")
    .toLowerCase()
    .replace(/\s+/g, "-")
    .trim() || "";

// Stable, key-sorted stringify so we can detect real content changes regardless of
// object key order. Used so Save uploads only the artifacts that actually changed.
const stableStringify = (val) => {
  const seen = new WeakSet();
  const norm = (v) => {
    if (v === null || typeof v !== "object") return v;
    if (seen.has(v)) return null;
    seen.add(v);
    if (Array.isArray(v)) return v.map(norm);
    return Object.keys(v).sort().reduce((acc, k) => {
      acc[k] = norm(v[k]);
      return acc;
    }, {});
  };
  try { return JSON.stringify(norm(val)); } catch { return JSON.stringify(val ?? null); }
};
const jsonChanged = (a, b) => stableStringify(a) !== stableStringify(b);

const CaptionDetailPage = () => {
  const { orgId, sceneId, captionId, subActionFile } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const alert = useAlert();
  const editorRef = useRef(null);
  // Snapshot of what is currently persisted in S3, so Save uploads only changes.
  const savedBaselineRef = useRef(null);
  // Recapture the baseline from the editor's own output on the next content ping
  // (set on load/hydration) — avoids a false "unsaved" from rebuilt-vs-loaded JSON.
  const needsBaselineRef = useRef(true);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [dirtyCaptionFiles, setDirtyCaptionFiles] = useState([]);
  const [graphicDirty, setGraphicDirty] = useState(false);
  const [sceneName, setSceneName] = useState(() => {
    const fromState = location.state?.sceneName;
    if (fromState) return fromState;
    const cached = orgId ? sceneCache.getScenesByOrg(orgId) : null;
    const scenes = cached?.scenes || [];
    const scene = sceneId ? scenes.find((s) => (s._id || s.id) === sceneId) : null;
    return scene ? (scene.name || scene.scene_name || sceneId) : "";
  });
  const [captions, setCaptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organizationName, setOrganizationName] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingCaption, setLoadingCaption] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  /** When creating new caption, graphic ID/name from editor for breadcrumb */
  const [editorGraphicLabel, setEditorGraphicLabel] = useState("");
  /** Lottie versions within current caption version (from lottie/ folder, keyed by "v" field) */
  const [lottieVersionList, setLottieVersionList] = useState([]);
  /** Selected Lottie version: '' = Current (lottieAnimation.json), else e.g. '5.12.2' for lottie/5.12.2.json */
  const [selectedLottieVersion, setSelectedLottieVersion] = useState("");
  /** Sub-action list from subActions/ folder (when in subaction mode) */
  const [subActionList, setSubActionList] = useState([]);
  /** True while "Make this version in use" is in progress (show loader in Save button) */
  const [lottieVersionSaving, setLottieVersionSaving] = useState(false);
  /** True while lottieAnimation.json is being fetched in background */
  const [lottieHydrating, setLottieHydrating] = useState(false);

  // Use scene name from navigation state or cache immediately (avoids breadcrumb "..." delay)
  useEffect(() => {
    const fromState = location.state?.sceneName;
    if (fromState) {
      setSceneName(fromState);
      return;
    }
    const cached = orgId ? sceneCache.getScenesByOrg(orgId) : null;
    const scenes = cached?.scenes || [];
    const scene = sceneId ? scenes.find((s) => (s._id || s.id) === sceneId) : null;
    if (scene) setSceneName(scene.name || scene.scene_name || sceneId);
  }, [orgId, sceneId, location.state?.sceneName]);

  // Load organizations and scenes in parallel when we have orgId/sceneId; cache for back-navigation
  useEffect(() => {
    if (!orgId || !sceneId) return;
    setLoading(true);
    Promise.all([
      apiService.getOrganizationsFromScene(),
      apiService.getScenesByOrganization(orgId),
    ])
      .then(([orgsData, scenesData]) => {
        const orgs = Array.isArray(orgsData) ? orgsData : (orgsData?.organizations || orgsData);
        setOrganizations(orgs);
        sceneCache.setOrganizations(orgs);
        sceneCache.setScenesByOrg(orgId, scenesData);
        const scenes = scenesData?.scenes || [];
        const scene = scenes.find((s) => (s._id || s.id) === sceneId);
        if (scene) {
          setSceneName(scene.name || scene.scene_name || sceneId);
          setCaptions(scene.captions || []);
        }
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, [orgId, sceneId]);

  const org = organizations.find((o) => (o._id || o.id) === orgId);
  const orgNameForS3 = toPathSegment(org?.name);
  const sceneNameForS3 = toPathSegment(sceneName);

  const currentCaption = (captions || []).find((c) => (c.id || c._id) === captionId);
  const runningVersion = currentCaption?.caption_version || currentCaption?.version || "";

  // Profiles available to this scene are no longer picked on the scene form —
  // they're derived from the scene's organisation: Organization.competitions[]
  // (AeroBackend.User-Management-Service) lists the competitions the org is
  // entitled to, and BB_DataDistributionService's `competition` resolver
  // already enforces that allowlist server-side when queried with org_id and
  // no competition_id (see resolver.service.js + org/orgAccess.js). Each
  // competition document carries its own profile_id (bb_merge_db.competition).
  //
  // The org lookup hits UMS's authenticated /organization/:id, so it needs a
  // token — but getStaticData's resolver reads it from the `token` field of
  // the GraphQL `context` VARIABLE (see query.resolvers.js), not from any
  // HTTP header. Every other DDS call here (season, fixtures) ignores it
  // harmlessly, so it's simplest to always include it rather than special-case
  // this one query.
  const [orgCompetitions, setOrgCompetitions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!orgId) return undefined;
    const accessToken = authService.getAccessToken();
    dds.getStaticData({
      keys: ["competition.competition_id", "competition.profile_id", "competition.competition_name"],
      context: { org_id: orgId, token: accessToken ? `Bearer ${accessToken}` : null },
    })
      .then((res) => {
        if (!cancelled) setOrgCompetitions(res?.competition || []);
      })
      .catch((err) => {
        console.error(`Failed to load competitions for organisation "${orgId}":`, err);
        if (!cancelled) setOrgCompetitions([]);
      });
    return () => { cancelled = true; };
  }, [orgId]);

  // One entry per distinct profile_id among the org's allowed competitions —
  // multiple competitions can share a profile, so this is a dedupe, not a
  // 1:1 map. Falls back to the football_pls default when the org has no
  // competitions resolved yet (still loading, or genuinely none configured),
  // matching pre-org-scoping behaviour.
  const orgProfiles = useMemo(() => {
    const seen = new Map();
    for (const c of orgCompetitions) {
      if (c?.profile_id && !seen.has(c.profile_id)) seen.set(c.profile_id, c);
    }
    return seen.size > 0
      ? Array.from(seen.values())
      : [{ profile_id: "football_pls", competition_id: null }];
  }, [orgCompetitions]);

  // Resolved per-profile: profile_id -> fixtureListPreset response ({fixture: [...]}
  // or null while loading/on failure). Keyed by profile_id so results from a
  // previous organisation don't leak onto a profile they don't belong to.
  const [profileFixtureData, setProfileFixtureData] = useState({});

  // For each of the org's profiles, resolve its active season (season_status
  // "yes") off the competition_id already known from orgProfiles, then that
  // season's fixture list.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const entries = await Promise.all(orgProfiles.map(async (p) => {
        const profileId = p.profile_id;
        if (!profileId) return [p.competition_id || "unknown", null];
        try {
          let competitionId = p.competition_id;
          if (!competitionId) {
            const competitionRes = await dds.getStaticData({
              keys: ["competition.competition_id", "competition.competition_name"],
              context: { profile_id: profileId },
            });
            competitionId = competitionRes?.competition?.[0]?.competition_id;
          }
          if (!competitionId) return [profileId, null];

          const seasonRes = await dds.getStaticData({
            keys: ["season.season_id", "season.season_status"],
            context: { competition_id: competitionId },
          });
          const seasons = seasonRes?.season || [];
          const activeSeason = seasons.find((s) => s.season_status === "yes") || seasons[0];
          if (!activeSeason?.season_id) return [profileId, null];

          const fixtures = await dds.getStaticData(fixtureListPreset({
            competition_id: competitionId,
            season_id: activeSeason.season_id,
            page: 1,
            limit: 3000,
          }));
          return [profileId, fixtures];
        } catch (err) {
          console.error(`DDS season/fixture lookup failed for profile "${profileId}":`, err);
          return [profileId, null];
        }
      }));
      if (!cancelled) setProfileFixtureData(Object.fromEntries(entries));
    })();

    return () => { cancelled = true; };
  }, [orgProfiles]);

  const dataModels = useMemo(() => {
    return orgProfiles.map((p) => {
      const profileId = p.profile_id;
      const live = profileFixtureData[profileId];
      // The live chain can resolve to something truthy but empty (no matching
      // competition/season, or a season with no fixtures yet) — only trust it
      // once it actually carries fixtures. plsFootballData is a hand-authored
      // Premier League sample: it's only a meaningful stand-in for the actual
      // football_pls profile. Falling back to it for any OTHER profile would
      // silently show Premier League fixtures mislabeled as that profile —
      // worse than showing nothing, since it looks like real data.
      const hasLiveFixtures = Array.isArray(live?.fixture) && live.fixture.length > 0;
      const emptyFixtureData = { meta: { total: 0 }, fixture: [] };
      console.log(`Profile "${profileId}" has ${hasLiveFixtures ? live.fixture.length : 0} fixtures; using ${hasLiveFixtures ? "live" : (profileId === "football_pls" ? "plsFootballData" : "emptyFixtureData")} fallback.`);
      const fallback = profileId === "football_pls" ? plsFootballData : emptyFixtureData;
      return {
        id: profileId,
        name: p.competition_name || p.profile_id,
        profileId,
        data: hasLiveFixtures ? live : fallback,
      };
    });
  }, [orgProfiles, profileFixtureData]);

  // Tracks which model the dropdown is ACTUALLY showing right now, reported by
  // CaptionEditor's onDataModelChange (see BB_CaptionCreationApp/src/App.js —
  // it fires whenever the resolved model changes, whether that's from the
  // dropdown, the initial default, or a stored/previous selection). Without
  // this, CaptionDetailPage has no visibility into the dropdown's live state
  // at all — it's fully internal to the third-party editor.
  const [liveDataModelProfileId, setLiveDataModelProfileId] = useState(null);
  const handleDataModelChange = useCallback((model) => {
    setLiveDataModelProfileId(model?.profileId || model?.id || null);
  }, []);
  // Switching captions must not carry the previous caption's live selection
  // over as a false "current" pick for a caption that's never been opened.
  useEffect(() => {
    setLiveDataModelProfileId(null);
  }, [captionId]);

  // Priority: a real saved binding on the caption (once captionPayload.js
  // carries profile_id) > whatever the dropdown is currently showing (fed back
  // so this stays in sync rather than fighting the user's own selection) >
  // index 0 as the only sane default before either of those exists yet.
  const dataModelProfileId = currentCaption?.profile_id || liveDataModelProfileId || dataModels[0]?.profileId || "";

  useEffect(() => {
    if (captionId && captionId !== "new" && !String(captionId).startsWith("gfx_New_")) {
      setSelectedLottieVersion("");
    }
  }, [captionId]);

  // Full load: when caption or scene changes. Sub-action mode loads only sub-action JSON as full Maestro.
  useEffect(() => {
    let cancelled = false;
    const isPlaceholderNew = captionId && String(captionId).startsWith("gfx_New_");
    const shouldLoad = (
      captionId &&
      captionId !== "new" &&
      !isPlaceholderNew &&
      orgNameForS3 &&
      sceneNameForS3 &&
      (subActionFile || captions.length > 0)
    );
    if (shouldLoad) {
      setLoadingCaption(true);
      setInitialData(null);
      (async () => {
        try {
          if (subActionFile) {
            // Sub-action = full Maestro definition (component-in-component). Load sub-action and list subActions/.
            const [subActionData, subActionListRes] = await Promise.all([
              S3ApiService.loadCaptionJson({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId,
                version: runningVersion,
                fileName: `subActions/${subActionFile}.json`,
              }),
              S3ApiService.listCaptionObjects({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId,
                version: runningVersion,
                subPath: "subActions",
              }),
            ]);
            const maestro = subActionData != null && typeof subActionData === "object" ? subActionData : {};
            const files = (subActionListRes.files || []).filter((f) => f.name && f.name.endsWith(".json"));
            setSubActionList(files.map((f) => f.name.replace(/\.json$/i, "")));
            setLottieVersionList([]);
            setInitialData({ maestro });
          } else {
            const withTimeout = (promise, ms, fallback = null) =>
              Promise.race([
                promise,
                new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
              ]);

            // Parent caption: keep Maestro as primary, bound optional calls with timeouts
            const lottieListPromise = withTimeout(
              S3ApiService.listCaptionObjects({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId,
                version: runningVersion,
                subPath: "lottie",
              }),
              2500,
              { files: [] }
            );
            const subActionListPromise = withTimeout(
              S3ApiService.listCaptionObjects({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId,
                version: runningVersion,
                subPath: "subActions",
              }),
              1800,
              { files: [] }
            );
            const lottieFetchPromise = S3ApiService.loadCaptionJson({
              organizationName: orgNameForS3,
              sceneName: sceneNameForS3,
              captionId,
              version: runningVersion,
              fileName: "lottieAnimation.json",
            });
            const lottiePromise = withTimeout(lottieFetchPromise, 2500, null);
            const presetsFromS3Promise = withTimeout(
              S3ApiService.loadCaptionJson({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId,
                version: runningVersion,
                fileName: "presets.json",
              }),
              2500,
              null
            );
            const captionFilesPromise = (async () => {
              // "captions" (plural) is canonical; "caption" (singular) is a legacy read fallback.
              const folders = ["captions", "caption"];
              const loadedCaptionFiles = {};
              for (const folder of folders) {
                const listing = await withTimeout(
                  S3ApiService.listCaptionObjects({
                    organizationName: orgNameForS3,
                    sceneName: sceneNameForS3,
                    captionId,
                    version: runningVersion,
                    subPath: folder,
                  }),
                  1800,
                  { files: [] }
                );
                const files = (listing?.files || []).filter((f) => f.name && f.name.endsWith(".json"));
                if (files.length === 0) continue;
                const loaded = await Promise.all(
                  files.map((f) =>
                    withTimeout(
                      S3ApiService.loadCaptionJson({
                        organizationName: orgNameForS3,
                        sceneName: sceneNameForS3,
                        captionId,
                        version: runningVersion,
                        fileName: `${folder}/${f.name}`,
                      }),
                      1200,
                      null
                    )
                  )
                );
                files.forEach((f, index) => {
                  const key = (f.name || "").replace(/\.json$/i, "");
                  const data = loaded[index];
                  if (!key || !data || typeof data !== "object") return;
                  if (folder === "captions" || loadedCaptionFiles[key] == null) {
                    loadedCaptionFiles[key] = data;
                  }
                });
              }
              return loadedCaptionFiles;
            })();
            const maestro = await (async () => {
              const capMeta = (captions || []).find((c) => (c.id || c._id) === captionId) || {};
              const candidateNames = Array.from(new Set([
                captionId,
                capMeta.caption_name,
                capMeta.name,
                capMeta.captionId,
              ].filter(Boolean).map((v) => String(v).trim()).filter(Boolean)));

              // Try a few likely maestro file names; some captions are stored by caption id,
              // others by graphic/manifest id (caption_name/name).
              for (const name of candidateNames) {
                const fileName = `${name}.json`;
                const loaded = await withTimeout(
                  S3ApiService.loadCaptionJson({
                    organizationName: orgNameForS3,
                    sceneName: sceneNameForS3,
                    captionId,
                    version: runningVersion,
                    fileName,
                  }),
                  1800,
                  null
                );
                if (loaded && typeof loaded === "object") {
                  return loaded;
                }
              }

              // Discover root-level maestro json by listing caption version root.
              // This handles cases where the file name is neither captionId nor caption_name.
              try {
                const rootList = await withTimeout(
                  S3ApiService.listCaptionObjects({
                    organizationName: orgNameForS3,
                    sceneName: sceneNameForS3,
                    captionId,
                    version: runningVersion,
                    subPath: "",
                  }),
                  1800,
                  { files: [] }
                );
                const rootJsonFiles = (rootList?.files || [])
                  .map((f) => f?.name)
                  .filter((name) => typeof name === "string" && name.endsWith(".json"))
                  .filter((name) => name !== "lottieAnimation.json" && name !== "presets.json")
                  .filter((name) => !name.startsWith("subActions/") && !name.startsWith("presets/") && !name.startsWith("lottie/"));

                const preferredOrder = [
                  ...rootJsonFiles.filter((n) => n.startsWith("gfx_")),
                  ...rootJsonFiles.filter((n) => !n.startsWith("gfx_")),
                ];

                for (const fileName of preferredOrder) {
                  const loaded = await withTimeout(
                    S3ApiService.loadCaptionJson({
                      organizationName: orgNameForS3,
                      sceneName: sceneNameForS3,
                      captionId,
                      version: runningVersion,
                      fileName,
                    }),
                    1500,
                    null
                  );
                  if (loaded && typeof loaded === "object") {
                    return loaded;
                  }
                }
              } catch (_) { }

              // Slow fallback retries without timeout.
              for (const name of candidateNames) {
                const fileName = `${name}.json`;
                try {
                  const loaded = await S3ApiService.loadCaptionJson({
                    organizationName: orgNameForS3,
                    sceneName: sceneNameForS3,
                    captionId,
                    version: runningVersion,
                    fileName,
                  });
                  if (loaded && typeof loaded === "object") {
                    return loaded;
                  }
                } catch (_) { }
              }
              return {};
            })();
            const [lottieList, lottie, presetsFromS3, subActionListRes, captionFilesFromS3] = await Promise.all([
              lottieListPromise,
              lottiePromise,
              presetsFromS3Promise,
              subActionListPromise,
              captionFilesPromise,
            ]);

            const lottieVersions = (lottieList?.files || [])
              .filter((f) => f.name && f.name.endsWith(".json"))
              .map((f) => f.name.replace(/\.json$/i, ""));
            setLottieVersionList(lottieVersions);

            const subActionFiles = (subActionListRes?.files || []).filter((f) => f.name && f.name.endsWith(".json"));
            const subActionNames = subActionFiles.map((f) => f.name.replace(/\.json$/i, ""));
            setSubActionList(subActionNames);

            let presets = undefined;
            if (presetsFromS3 != null && typeof presetsFromS3 === "object") {
              if (Array.isArray(presetsFromS3)) {
                presets = presetsFromS3;
              } else if (Array.isArray(presetsFromS3.presets)) {
                presets = presetsFromS3.presets;
              }
            }
            if ((!presets || presets.length === 0) && orgNameForS3 && sceneNameForS3) {
              try {
                const list = await withTimeout(
                  S3ApiService.listCaptionObjects({
                    organizationName: orgNameForS3,
                    sceneName: sceneNameForS3,
                    captionId,
                    version: runningVersion,
                    subPath: "presets",
                  }),
                  1800,
                  { files: [] }
                );
                const files = (list.files || []).filter((f) => f.name && f.name.endsWith(".json"));
                if (files.length > 0) {
                  const loaded = await Promise.all(
                    files.slice(0, 20).map((f) =>
                      withTimeout(
                        S3ApiService.loadCaptionJson({
                          organizationName: orgNameForS3,
                          sceneName: sceneNameForS3,
                          captionId,
                          version: runningVersion,
                          fileName: "presets/" + f.name,
                        }),
                        1200,
                        null
                      )
                    )
                  );
                  presets = loaded
                    .filter(Boolean)
                    .map((data, i) => ({
                      id: (files[i].name || "").replace(/\.json$/i, ""),
                      name: (files[i].name || "").replace(/\.json$/i, ""),
                      data: typeof data === "object" && data !== null ? data : {},
                    }));
                }
              } catch (_) { }
            }
            // Merge subAction files into v_subActions enum so AE LIVE shows them and JSON output contains them
            if (subActionNames.length > 0 && maestro && typeof maestro === "object") {
              const existing = maestro.v_subActions && typeof maestro.v_subActions === "object" ? maestro.v_subActions : {};
              const safeEnum = Array.isArray(existing?.properties?.subAction?.enum)
                ? existing.properties.subAction.enum
                : [];
              const safeEnumNames = Array.isArray(existing?.properties?.subAction?.enumNames)
                ? existing.properties.subAction.enumNames
                : [];
              const safeRefMap = existing?.refMap && typeof existing.refMap === "object"
                ? existing.refMap
                : {};
              const mergedEnum = Array.from(new Set([...safeEnum, ...subActionNames]));
              const mergedEnumNames = mergedEnum.map((val) => {
                const idx = safeEnum.indexOf(val);
                return idx >= 0 ? (safeEnumNames[idx] || val) : val;
              });
              const mergedRefMap = { ...safeRefMap };
              mergedEnum.forEach((name) => {
                if (!name || name === "None") return;
                const current = mergedRefMap[name];
                if (typeof current === "string" && current.trim()) return;
                mergedRefMap[name] = `#/$defs/${name}`;
              });
              maestro.v_subActions = {
                title: existing.title || "SubAction",
                ...existing,
                refMap: mergedRefMap,
                properties: {
                  ...(existing.properties || {}),
                  subAction: {
                    ...(existing.properties?.subAction || {}),
                    type: "string",
                    enum: mergedEnum,
                    enumNames: mergedEnumNames,
                  },
                },
              };
            }

            setInitialData({
              maestro: maestro || undefined,
              lottie: lottie || undefined,
              presets,
              captionFiles: captionFilesFromS3 && typeof captionFilesFromS3 === "object"
                ? captionFilesFromS3
                : {},
            });

            // If lottie timed out during initial load, continue same in-flight request in background.
            if (!lottie) {
              setLottieHydrating(true);
              lottieFetchPromise
                .then((lateLottie) => {
                  if (cancelled || lateLottie == null) return;
                  setInitialData((prev) => {
                    if (!prev || prev.lottie) return prev;
                    return { ...prev, lottie: lateLottie };
                  });
                })
                .catch(() => { })
                .finally(() => {
                  if (!cancelled) setLottieHydrating(false);
                });
            }
          }
        } catch (err) {
          console.error("Load caption files:", err);
          setInitialData({});
          setSubActionList([]);
        } finally {
          setLoadingCaption(false);
        }
      })();
    } else if (captionId === "new" || !captionId || isPlaceholderNew) {
      // Pre-populate version from scene/navigation state or default to v1.0
      const defaultVersion = location.state?.defaultVersion || "v1.0";
      setInitialData({
        maestro: {
          version: defaultVersion,
          captionVersion: defaultVersion,
        },
      });
      setEditorGraphicLabel("");
      setSubActionList([]);
      setLottieHydrating(false);
    } else {
      setSubActionList([]);
      setLottieHydrating(false);
    }
    return () => {
      cancelled = true;
    };
  }, [captionId, orgNameForS3, sceneNameForS3, captions, runningVersion, subActionFile]);

  // Lottie-only switch: when only selectedLottieVersion changes and we already have data. Skip in subaction mode.
  // Do not include initialData in deps: this effect updates initialData, so including it would cause an infinite loop.
  useEffect(() => {
    const isPlaceholderNew = captionId && String(captionId).startsWith("gfx_New_");
    if (
      subActionFile ||
      !captionId ||
      captionId === "new" ||
      isPlaceholderNew ||
      !orgNameForS3 ||
      !sceneNameForS3 ||
      !initialData
    ) {
      return;
    }
    const fileName = selectedLottieVersion
      ? `lottie/${selectedLottieVersion}.json`
      : "lottieAnimation.json";
    let cancelled = false;
    S3ApiService.loadCaptionJson({
      organizationName: orgNameForS3,
      sceneName: sceneNameForS3,
      captionId,
      version: runningVersion,
      fileName,
    }).then((lottie) => {
      if (!cancelled && lottie != null) {
        setInitialData((prev) => (prev ? { ...prev, lottie } : prev));
      }
    });
    return () => { cancelled = true; };
  }, [selectedLottieVersion, subActionFile, captionId, orgNameForS3, sceneNameForS3, runningVersion]);

  const handleCaptionChange = (event) => {
    const nextId = event.target.value;
    if (nextId && nextId !== captionId) {
      navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/${nextId}`);
    }
  };

  const handleSubActionChange = (event) => {
    const next = event.target.value;
    if (next && next !== subActionFile) {
      navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/${captionId}/subaction/${encodeURIComponent(next)}`);
    }
  };

  const handleManifestChange = useCallback((manifest) => {
    const id = manifest?.id || manifest?.name || "";
    setEditorGraphicLabel(typeof id === "string" ? id : "");
  }, []);
  const handleSelectLottieVersion = useCallback((ver) => setSelectedLottieVersion(ver || ""), []);
  const handleEditSubAction = useCallback(
    (name) => {
      navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/${captionId}/subaction/${encodeURIComponent(name)}`);
    },
    [captionId, orgId, sceneId, navigate]
  );

  const handleDeletePreset = useCallback(
    async (presetId) => {
      if (!presetId || !orgNameForS3 || !sceneNameForS3 || !captionId || captionId === "new") return;
      const cap = (captions || []).find((c) => (c.id || c._id) === captionId);
      const version = cap?.caption_version || cap?.version || runningVersion || "";
      const prefix = S3ApiService.getCaptionPrefix(captionId, version);
      const key = `${prefix}presets/${presetId}.json`;
      try {
        await S3ApiService.deleteObject({ key, organizationName: orgNameForS3, sceneName: sceneNameForS3 });
        console.log(`Deleted S3 preset: ${key}`);
      } catch (err) {
        console.warn(`Failed to delete S3 preset ${key}:`, err);
      }
    },
    [captionId, captions, orgNameForS3, sceneNameForS3, runningVersion]
  );

  const handleDeleteSubAction = useCallback(
    async (name) => {
      if (!name || !orgNameForS3 || !sceneNameForS3 || !captionId || captionId === "new") return;
      const cap = (captions || []).find((c) => (c.id || c._id) === captionId);
      const version = cap?.caption_version || cap?.version || runningVersion || "";
      const prefix = S3ApiService.getCaptionPrefix(captionId, version);
      const key = `${prefix}subActions/${name}.json`;
      try {
        await S3ApiService.deleteObject({ key, organizationName: orgNameForS3, sceneName: sceneNameForS3 });
        console.log(`Deleted S3 sub-action: ${key}`);
      } catch (err) {
        console.warn(`Failed to delete S3 sub-action ${key}:`, err);
      }
    },
    [captionId, captions, orgNameForS3, sceneNameForS3, runningVersion]
  );

  const handleSetLottieVersionAsCurrent = async (version) => {
    if (!version || !orgNameForS3 || !sceneNameForS3 || !captionId || captionId === "new") return;
    const cap = (captions || []).find((c) => (c.id || c._id) === captionId);
    const versionPath = cap?.caption_version || cap?.version || "";
    setLottieVersionSaving(true);
    try {
      const lottieData = await S3ApiService.loadCaptionJson({
        organizationName: orgNameForS3,
        sceneName: sceneNameForS3,
        captionId,
        version: versionPath,
        fileName: `lottie/${version}.json`,
      });
      if (lottieData == null) {
        alert.error("Lottie file not found for this version.");
        return;
      }
      await S3ApiService.saveCaptionJson({
        organizationName: orgNameForS3,
        sceneName: sceneNameForS3,
        captionId,
        version: versionPath,
        fileName: "lottieAnimation.json",
        data: lottieData,
      });
      setSelectedLottieVersion("");
      setInitialData((prev) => (prev ? { ...prev, lottie: lottieData } : prev));
      alert.success(`Lottie version ${version} is now in use.`, "Lottie version updated");
    } catch (err) {
      console.error("Set Lottie version as current:", err);
      alert.error(err?.message || "Failed to set Lottie version as current.", "Error");
    } finally {
      setLottieVersionSaving(false);
    }
  };

  // When a caption loads or hydrates, recapture the saved-baseline from the editor's
  // own output on its next content ping, and clear the unsaved indicator.
  useEffect(() => {
    needsBaselineRef.current = true;
    setHasUnsaved(false);
    setDirtyCaptionFiles([]);
    setGraphicDirty(false);
  }, [initialData]);

  // Editor pings on every content change. The first ping after a (re)load captures
  // the clean baseline (used for both the unsaved indicator and diff-based Save);
  // later pings compare against it to flag unsaved changes.
  const handleContentChange = useCallback(() => {
    const data = editorRef.current?.getCaptionData?.();
    if (!data) return;
    const snap = {
      maestro: data.maestro,
      lottie: data.lottie ?? null,
      captionFiles: data.captionFiles || {},
    };
    if (needsBaselineRef.current) {
      savedBaselineRef.current = snap;
      needsBaselineRef.current = false;
      setHasUnsaved(false);
      setDirtyCaptionFiles([]);
      setGraphicDirty(false);
      return;
    }
    const b = savedBaselineRef.current || {};
    const baseFiles = b.captionFiles || {};
    const names = Array.from(new Set([...Object.keys(snap.captionFiles), ...Object.keys(baseFiles)]));
    const dirtyFiles = names.filter((n) => jsonChanged(snap.captionFiles[n], baseFiles[n]));
    const maestroDirty = jsonChanged(snap.maestro, b.maestro);
    const lottieDirty = jsonChanged(snap.lottie, b.lottie);
    setDirtyCaptionFiles(dirtyFiles);
    setGraphicDirty(maestroDirty);
    setHasUnsaved(maestroDirty || lottieDirty || dirtyFiles.length > 0);
  }, []);

  const handleSave = async () => {
    if (!editorRef.current?.getCaptionData) return;
    const payload = editorRef.current.getCaptionData();
    const cap = (captions || []).find((c) => (c.id || c._id) === captionId);
    const version = cap?.caption_version || cap?.version || "v1.0";

    setSaving(true);
    setSaveError(null);
    try {
      if (subActionFile && orgNameForS3 && sceneNameForS3) {
        // Sub-action mode: save only maestro to subActions/file.json (full Maestro = component-in-component)
        const maestro = payload.maestro ?? (typeof payload.maestro === "string" ? (() => { try { return JSON.parse(payload.maestro); } catch { return {}; } })() : {});
        await S3ApiService.saveCaptionJson({
          organizationName: orgNameForS3,
          sceneName: sceneNameForS3,
          captionId,
          version,
          fileName: `subActions/${subActionFile}.json`,
          data: maestro,
        });
        // Refresh dropdown list after save so newly created/renamed subactions appear without a page reload
        try {
          const subActionListRes = await S3ApiService.listCaptionObjects({
            organizationName: orgNameForS3,
            sceneName: sceneNameForS3,
            captionId,
            version,
            subPath: "subActions",
          });
          const files = (subActionListRes.files || []).filter((f) => f.name && f.name.endsWith(".json"));
          setSubActionList(files.map((f) => f.name.replace(/\.json$/i, "")));
        } catch (err) {
          console.warn("SubAction list refresh failed:", err);
        }
        savedBaselineRef.current = {
          maestro: payload.maestro,
          lottie: payload.lottie ?? null,
          captionFiles: payload.captionFiles || {},
        };
        needsBaselineRef.current = false;
        setHasUnsaved(false);
        setDirtyCaptionFiles([]);
        setGraphicDirty(false);
        alert.success(`Sub-action ${subActionFile} saved.`, "Saved");
        setSaving(false);
        if (isNewCaption) {
          navigate(`/dashboard/scene-management/${orgId}/${sceneId}/edit`, {
            state: { sceneName },
          });
        }
        return;
      }

      const idForS3 = captionId && captionId !== "new" ? captionId : (payload.manifest?.id || payload.maestro?.id || "gfx_NewCaption");
      const maestroId = typeof payload.maestro === "string" ? (() => { try { return JSON.parse(payload.maestro); } catch { return {}; } })() : (payload.maestro || {});
      const captionName = payload.manifest?.name || maestroId.name || idForS3;
      const captionDescription = payload.manifest?.description || maestroId.description || "";

      if (orgNameForS3 && sceneNameForS3) {
        const baseline = savedBaselineRef.current || {};
        const uploaded = [];

        // Maestro manifest — upload only when it changed since load / last save.
        if (jsonChanged(payload.maestro, baseline.maestro)) {
          await S3ApiService.saveCaptionJson({
            organizationName: orgNameForS3,
            sceneName: sceneNameForS3,
            captionId: idForS3,
            version,
            fileName: `${idForS3}.json`,
            data: payload.maestro,
          });
          uploaded.push(`${idForS3}.json`);
        }

        // Lottie is often the largest payload — upload (both files) only if it changed.
        if (payload.lottie != null && jsonChanged(payload.lottie, baseline.lottie)) {
          await S3ApiService.saveCaptionJson({
            organizationName: orgNameForS3,
            sceneName: sceneNameForS3,
            captionId: idForS3,
            version,
            fileName: "lottieAnimation.json",
            data: payload.lottie,
          });
          uploaded.push("lottieAnimation.json");
          const lottieV = payload.lottie && payload.lottie.v;
          if (lottieV != null && lottieV !== '') {
            const safeV = String(lottieV).replace(/[^a-zA-Z0-9._-]/g, '_').trim() || 'unknown';
            await S3ApiService.saveCaptionJson({
              organizationName: orgNameForS3,
              sceneName: sceneNameForS3,
              captionId: idForS3,
              version,
              fileName: `lottie/${safeV}.json`,
              data: payload.lottie,
            });
            uploaded.push(`lottie/${safeV}.json`);
          }
        }

        if (Array.isArray(payload.presets) && payload.presets.length > 0) {
          // Save combined presets.json
          await S3ApiService.saveCaptionJson({
            organizationName: orgNameForS3,
            sceneName: sceneNameForS3,
            captionId: idForS3,
            version,
            fileName: "presets.json",
            data: payload.presets,
          });
          // Save individual presets/Name.json files (matching S3 folder structure)
          await Promise.all(
            payload.presets.map((preset) => {
              const presetFileName = preset.id || preset.name;
              if (!presetFileName) return Promise.resolve();
              return S3ApiService.saveCaptionJson({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId: idForS3,
                version,
                fileName: `presets/${presetFileName}.json`,
                data: preset.data ?? preset,
              });
            })
          );
        }

        if (payload.captionFiles && typeof payload.captionFiles === "object") {
          const captionEntries = Object.entries(payload.captionFiles).filter(
            ([name, data]) => name && data && typeof data === "object"
          );
          const expectedNames = new Set(captionEntries.map(([name]) => `${name}.json`));
          const baseCaptionFiles =
            baseline.captionFiles && typeof baseline.captionFiles === "object" ? baseline.captionFiles : {};

          // Upload only caption files whose content changed (or are new).
          const changedEntries = captionEntries.filter(([name, data]) => jsonChanged(data, baseCaptionFiles[name]));
          await Promise.all(
            changedEntries.map(([name, data]) =>
              S3ApiService.saveCaptionJson({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId: idForS3,
                version,
                fileName: `captions/${name}.json`,
                data,
              })
            )
          );
          changedEntries.forEach(([name]) => uploaded.push(`captions/${name}.json`));

          // Only list+reconcile when caption files were removed/renamed. Pure content
          // edits and additions need no stale cleanup, so we skip the two LIST calls.
          const removedNames = Object.keys(baseCaptionFiles)
            .map((n) => `${n}.json`)
            .filter((n) => !expectedNames.has(n));

          if (removedNames.length > 0) {
            const [captionList, captionsList] = await Promise.all([
              S3ApiService.listCaptionObjects({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId: idForS3,
                version,
                subPath: "caption",
              }).catch(() => ({ files: [] })),
              S3ApiService.listCaptionObjects({
                organizationName: orgNameForS3,
                sceneName: sceneNameForS3,
                captionId: idForS3,
                version,
                subPath: "captions",
              }).catch(() => ({ files: [] })),
            ]);

            const staleInCaption = (captionList?.files || [])
              .map((f) => f?.name)
              .filter((name) => typeof name === "string" && name.endsWith(".json") && !expectedNames.has(name));
            const staleInCaptions = (captionsList?.files || [])
              .map((f) => f?.name)
              .filter((name) => typeof name === "string" && name.endsWith(".json") && !expectedNames.has(name));

            const prefix = S3ApiService.getCaptionPrefix(idForS3, version);
            await Promise.all([
              ...staleInCaption.map((name) =>
                S3ApiService.deleteObject({
                  key: `${prefix}caption/${name}`,
                  organizationName: orgNameForS3,
                  sceneName: sceneNameForS3,
                }).catch((err) => console.warn(`Failed deleting caption/${name}:`, err))
              ),
              ...staleInCaptions.map((name) =>
                S3ApiService.deleteObject({
                  key: `${prefix}captions/${name}`,
                  organizationName: orgNameForS3,
                  sceneName: sceneNameForS3,
                }).catch((err) => console.warn(`Failed deleting captions/${name}:`, err))
              ),
            ]);
          }
        }

        // Apply scene base template (captionComponent.mjs + SceneIndex/) — mirrors Lambda logic
        try {
          await apiService.applySceneTemplate({
            organizationName: orgNameForS3,
            sceneName: sceneNameForS3,
            captionId: idForS3,
            version,
          });
        } catch (templateErr) {
          console.warn("Scene template apply failed (non-critical):", templateErr?.message);
        }

        // Refresh the baseline so a subsequent Save re-uploads only what changes next.
        savedBaselineRef.current = {
          maestro: payload.maestro,
          lottie: payload.lottie ?? null,
          captionFiles: { ...(payload.captionFiles || {}) },
        };
        needsBaselineRef.current = false;
        setHasUnsaved(false);
        setDirtyCaptionFiles([]);
        setGraphicDirty(false);
        console.log(`Caption save \u2192 ${uploaded.length} file(s) uploaded:`, uploaded);
      }

      // Save only S3 files; scene API update happens on scene detail page Save
      const captionVersion = version || "";
      const allVersions = cap?.all_versions || [captionVersion];
      const captionEntry = {
        id: idForS3,
        caption_name: idForS3,
        description: captionDescription,
        version: captionVersion,
        all_versions: allVersions,
        existingFile: true,
        // Whichever profile the data-model dropdown is bound to right now —
        // see dataModelProfileId above. Only meaningful for a brand-new
        // caption here; changing it on an already-saved caption isn't wired
        // up yet (same as layer/disabled, which are only editable from the
        // Scene Details page's caption grid, not from here).
        profile_id: dataModelProfileId || "",
      };

      alert.success("Caption saved successfully.", "Saved");
      if (isNewCaption) {
        const existingCaptions = location.state?.existingCaptions || captions || [];
        navigate(`/dashboard/scene-management/${orgId}/${sceneId}/edit`, {
          state: {
            sceneName,
            addedCaption: captionEntry,
            existingCaptions,
            // OPUS-1391: hand back the layer the scene editor sent us, so a
            // caption created from the Front section returns to Front rather
            // than silently defaulting to Main.
            captionLayer: location.state?.captionLayer,
          },
        });
      }
    } catch (err) {
      setSaveError(err?.message || "Save failed");
      alert.error(err?.message || "Save failed", "Error");
    } finally {
      setSaving(false);
    }
  };

  const sceneEditUrl = `/dashboard/scene-management/${orgId}/${sceneId}/edit`;
  const captionLabel = currentCaption?.caption_name || currentCaption?.name || captionId || "Caption";
  /** Treat "new" or placeholder (gfx_New_*) as new: breadcrumb shows editor graphic ID in real time */
  const isNewCaption = !captionId || captionId === "new" || String(captionId).startsWith("gfx_New_");
  const breadcrumbCaptionLabel = isNewCaption
    ? (editorGraphicLabel && editorGraphicLabel.trim() ? editorGraphicLabel.trim() : "Add new caption")
    : captionLabel;
  // OPUS-1391: the caption's own layer once it exists; for one still being
  // created, the layer the scene editor sent us. Null when neither is known, so
  // the chip stays off rather than asserting a default that may not hold.
  const breadcrumbLayerValue = currentCaption?.layer || (isNewCaption ? location.state?.captionLayer : null);
  const breadcrumbLayer = breadcrumbLayerValue ? layerMeta(breadcrumbLayerValue) : null;
  const version = runningVersion;
  /** For existing caption (not new, not placeholder), show loading until initialData is set */
  const isEditingExisting = Boolean(
    captionId && captionId !== "new" && !String(captionId).startsWith("gfx_New_")
  );
  const captionDataReady = !isEditingExisting || initialData !== null;
  /** Assets tab only for parent caption (not sub-action, not new). Sub-action = component-in-component, no Assets. */
  const showAssetExplorer =
    !subActionFile &&
    orgNameForS3 &&
    sceneNameForS3 &&
    captionId &&
    captionId !== "new" &&
    !String(captionId).startsWith("gfx_New_");

  // Detect /captions/:captionId/assets/<splat> in the URL so the in-folder path
  // stays in sync with the URL while the user navigates. Refresh and deep-link
  // work — tab activation itself is owned by CaptionEditor (third-party).
  const assetsUrlSplat = useMemo(() => {
    const path = location.pathname || '';
    const marker = `/captions/${captionId}/assets`;
    const idx = path.indexOf(marker);
    if (idx === -1) return null;
    const rest = path.slice(idx + marker.length);
    return rest.replace(/^\/+/, '').replace(/\/+$/, '');
  }, [location.pathname, captionId]);

  const assetExplorerPath = assetExplorerUrlSegmentToLogical(assetsUrlSplat || '');

  const handleAssetExplorerPathChange = useCallback((nextLogical) => {
    const rel = assetExplorerLogicalToRelative(nextLogical);
    const base = `/dashboard/scene-management/${orgId}/${sceneId}/captions/${captionId}/assets`;
    const next = rel ? `${base}/${rel}` : base;
    if (next !== location.pathname) navigate(next);
  }, [orgId, sceneId, captionId, navigate, location.pathname]);

  const assetsTabContent = showAssetExplorer ? (
    <ThemeProvider theme={darkTheme}>
      <AssetExplorer
        organizationName={orgNameForS3}
        sceneName={sceneNameForS3}
        captionPathPrefix={`${captionId}/${version}`}
        path={assetExplorerPath}
        onPathChange={handleAssetExplorerPathChange}
      />
    </ThemeProvider>
  ) : null;

  const formPreviewRenderer = useCallback(
    (outputJson, context) => (
      <MaestroFormPreview
        maestroJson={outputJson}
        dataModel={context?.dataModel}
        captionFiles={context?.captionFiles}
        selectedCaption={context?.selectedCaption}
      />
    ),
    []
  );

  const breadcrumbContent = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
      {/* Scene Management */}
      <Link
        component="button" variant="body2" underline="hover" color="inherit" type="button"
        sx={{ fontSize: "inherit", lineHeight: 1.5 }}
        onClick={() => navigate("/dashboard/scene-management")}
      >
        Scene Management
      </Link>
      <Typography component="span" sx={{ color: "text.secondary", fontSize: "inherit" }}>/</Typography>

      {/* Scene name */}
      <Link
        component="button" variant="body2" underline="hover" color="inherit" type="button"
        sx={{ fontSize: "inherit", lineHeight: 1.5 }}
        onClick={() => navigate(sceneEditUrl, { state: { sceneName } })}
      >
        {(loading && !sceneName) ? "..." : (sceneName || sceneId)}
      </Link>
      <Typography component="span" sx={{ color: "text.secondary", fontSize: "inherit" }}>/</Typography>

      {subActionFile ? (
        /* ── In subaction mode: caption name is a back-link, then subaction dropdown ── */
        <>
          <Link
            component="button" variant="body2" underline="hover" color="inherit" type="button"
            sx={{ fontSize: "inherit", lineHeight: 1.5 }}
            onClick={() => navigate(`/dashboard/scene-management/${orgId}/${sceneId}/captions/${captionId}`)}
          >
            {breadcrumbCaptionLabel}
          </Link>
          <Typography component="span" sx={{ color: "text.secondary", fontSize: "inherit" }}>/</Typography>
          <FormControl size="small" sx={{ minWidth: 180, "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}>
            <Select
              value={subActionFile || ""}
              onChange={handleSubActionChange}
              displayEmpty
              renderValue={(v) => v || "Select sub-action"}
              sx={{ height: 32, fontSize: "inherit", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, border: "none" }}
            >
              {[...new Set([...(subActionList || []), subActionFile].filter(Boolean))].map((name) => (
                <MenuItem key={name} value={name}>{name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      ) : (
        /* ── Normal mode: caption dropdown with all captions ── */
        <FormControl size="small" sx={{ minWidth: 200, "& .MuiOutlinedInput-notchedOutline": { border: "none" } }}>
          <Select
            value={captionId || ""}
            onChange={handleCaptionChange}
            displayEmpty
            renderValue={(v) => (isNewCaption ? breadcrumbCaptionLabel : v ? captionLabel : "Select caption")}
            sx={{ height: 32, fontSize: "inherit", "& .MuiOutlinedInput-notchedOutline": { border: "none" }, "& .MuiSelect-select": { pl: 0.5 }, border: "none" }}
          >
            <MenuItem value="new">Add new caption</MenuItem>
            {(captions || []).map((cap) => {
              const id = cap.id || cap._id;
              const name = cap.caption_name || cap.name || id;
              return <MenuItem key={id} value={id}>{name}</MenuItem>;
            })}
          </Select>
        </FormControl>
      )}

      {/* OPUS-1391: which player layer this caption renders into. Read-only
          here — the layer is owned by the scene editor; this is just so you can
          tell where you are without navigating back. */}
      {breadcrumbLayer && (
        <Box
          title={`Renders on the ${breadcrumbLayer.label} layer — change it in the scene editor`}
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, height: 22, borderRadius: "999px", border: "1px solid var(--border)", background: "var(--surface-2)", flexShrink: 0 }}
        >
          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: breadcrumbLayer.color, flexShrink: 0 }} />
          <Box component="span" sx={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)" }}>
            {breadcrumbLayer.label}
          </Box>
        </Box>
      )}
    </Box>
  );

  const isSaveBusy = saving || lottieVersionSaving;
  const rightAction = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
      {lottieHydrating && !subActionFile && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            Lottie downloading...
          </Typography>
        </Box>
      )}
      {saveError && (
        <Typography variant="caption" color="error">
          {saveError}
        </Typography>
      )}
      {hasUnsaved && !isSaveBusy && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "warning.main" }} />
          <Typography variant="caption" sx={{ color: "warning.main", fontWeight: 600 }}>
            Unsaved changes
          </Typography>
        </Box>
      )}
      <Button
        variant="outlined"
        color="info"
        onClick={() => navigate(sceneEditUrl, { state: { sceneName } })}
        startIcon={<OpusIcon name="chevronLeft" size={18} />}
      >
        Back
      </Button>
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={isSaveBusy}
        startIcon={isSaveBusy ? <CircularProgress size={16} color="inherit" /> : null}
      >
        {lottieVersionSaving ? "Updating Lottie…" : saving ? "Saving..." : "Save"}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <DashboardBreadcrumbs
        breadcrumbContent={breadcrumbContent}
        rightAction={rightAction}
      />
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {!captionDataReady ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography color="text.secondary">
              {loading ? "Loading scene…" : "Loading caption data…"}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            <CaptionEditor
              key={subActionFile ? `${captionId}-subaction-${subActionFile}` : captionId || "new"}
              ref={editorRef}
              initialData={initialData ?? undefined}
              assetsTabContent={assetsTabContent}
              formPreviewRenderer={formPreviewRenderer}
              onManifestChange={handleManifestChange}
              onContentChange={handleContentChange}
              dirtyCaptionFiles={dirtyCaptionFiles}
              graphicDirty={graphicDirty}
              lottieVersionList={lottieVersionList}
              selectedLottieVersion={selectedLottieVersion}
              onSelectLottieVersion={handleSelectLottieVersion}
              onSetLottieVersionAsCurrent={handleSetLottieVersionAsCurrent}
              lottieDownloadInProgress={lottieHydrating}
              dataModels={dataModels}
              dataModelProfileId={dataModelProfileId}
              defaultDataModelId={dataModels[0]?.id}
              onDataModelChange={handleDataModelChange}
              subActionFileName={subActionFile ?? undefined}
              onEditSubAction={
                !subActionFile && captionId && captionId !== "new" && !String(captionId).startsWith("gfx_New_")
                  ? handleEditSubAction
                  : undefined
              }
              onDeletePreset={handleDeletePreset}
              onDeleteSubAction={handleDeleteSubAction}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CaptionDetailPage;
